import { Component, OnInit, ViewChild } from '@angular/core';
import { environment } from 'environments/environment';
import {
    ConfirmationPaymentMethod,
    DealerTerm,
    Delivery,
    DeliveryOption,
    EmailTemplate,
    InvoicePaymentMethod,
    PartType,
    PricingTier,
    PurchaseOrderLineItem,
    PurchaseOrderShipAddress,
    Setting,
    StateTaxExemption,
    VendorTerm,
} from 'app/core/model/entity-model';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AppInformationService, AuthService, GenesisUnitOfWork } from '../../core/core.module';
import { InputDialogComponent } from '../../shared/input-dialog/input-dialog.component';
import { SalesRepDialogComponent } from '../sales-rep-dialog/sales-rep-dialog.component';
import { DeliveryOptionDialogComponent } from '../delivery-option-dialog/delivery-option-dialog.component';
import { StateTaxExemptionDialogComponent } from '../state-tax-exemption-dialog/state-tax-exemption-dialog.component';
import { LineItemDialogComponent } from '../line-item-dialog/line-item-dialog.component';
import { FeeTypeDialogComponent } from '../fee-type-dialog/fee-type-dialog.component';
import { ConfirmationPaymentMethodDialogComponent } from '../confirmation-payment-method-dialog/confirmation-payment-method-dialog.component';
import { InvoicePaymentMethodDialogComponent } from '../invoice-payment-method-dialog/invoice-payment-method-dialog.component';
import { onToolbarPreparing } from 'app/core/utility';
import { DealerTermDialogComponent } from '../dealer-term-dialog/dealer-term-dialog.component';
import { EmailTemplateDialogComponent } from '../email-template-dialog copy/email-template-dialog.component';
import { VendorTermDialogComponent } from '../vendor-term-dialog/vendor-term-dialog.component';
import { PricingTierDialogComponent } from '../pricing-tier-dialog/pricing-tier-dialog.component';
import { PurchaseOrderShipAddressDialogComponent } from '../purchase-order-ship-address-dialog/purchase-order-ship-address-dialog.component';

@Component({
    selector: 'app-table-edit',
    templateUrl: './table-edit.component.html',
    styleUrls: ['./table-edit.component.scss'],
    standalone: false,
})
export class TableEditComponent implements OnInit {
    gridColumns: any[] = [];
    entities;
    entityTypes = [
        'Assembly Jigs',
        'Confirmation Payment Methods',
        'Credit Cards',
        'Dealer Terms',
        'Delivery Options',
        'Drivers',
        'Email Templates',
        'Expense Types',
        'Fee Types',
        'Hold Notes',
        'Invoice Payment Methods',
        'Order Payment Methods',
        'Part Service Charges',
        'Part Type',
        'Pricing Tiers',
        'Purchase Order Line Items',
        'Purchase Order Shipping Addresses',
        'Sales Rep',
        'State Tax Exemptions',
        'Trailers',
        'Trucks',
        'Transport Plates',
        'Vendor Payment Methods',
        'Vendor Terms',
    ];
    entityType;
    mileageRate: Setting;
    discountMileageRate: Setting;
    leadTime: Setting;
    buildLeadTime: Setting;
    dealerInactiveMonths: Setting;
    onTimeTargetPercent: Setting;
    dailyProductionCapacity: Setting;
    driverTripRecordUrlLastUpdate: Setting;

    // DEFAULTS : OVERRIDE AS NEEDED
    orderProperty = 'displayOrder';
    valueProperty = 'name';
    valuePrompt = 'Name';
    allowReorder = true;
    allowDelete = true;

    tripRecordUploadUrl = `${environment.serviceName}/file/SaveDriverTripRecordToBlobStorage`;
    uploadHeader: any;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onToolbarPreparing = onToolbarPreparing;

    constructor(
        private uow: GenesisUnitOfWork,
        private appInfo: AppInformationService,
        private modalService: BsModalService,
        private auth: AuthService,
    ) {}

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        this.mileageRate = (await this.uow.getSettingsByName('MileageRate')) as Setting;
        this.discountMileageRate = (await this.uow.getSettingsByName('DiscountMileageRate')) as Setting;
        this.leadTime = (await this.uow.getSettingsByName('LeadTime')) as Setting;
        this.buildLeadTime = (await this.uow.getSettingsByName('BuildLeadTime')) as Setting;
        this.dealerInactiveMonths = (await this.uow.getSettingsByName('DealerInactiveMonths')) as Setting;
        this.onTimeTargetPercent = (await this.uow.getSettingsByName('OnTimeTargetPercent')) as Setting;
        this.dailyProductionCapacity = (await this.uow.getSettingsByName('DailyProductionCapacity')) as Setting;
        this.driverTripRecordUrlLastUpdate = (await this.uow.getSettingsByName('DriverTripRecordUrlLastUpdate')) as Setting;
        if (this.entityTypes.length > 0) this.entityType = this.entityTypes[0];
        this.entityTypeValueChanged(null);
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async save() {
        await this.uow.commit();
    }
    cancel() {
        this.uow.rollback();
    }

    async entityTypeValueChanged(e) {
        this.setupForm();
        await this.getData();
    }

    setupForm() {
        switch (this.entityType) {
            case 'Assembly Jigs':
            case 'Credit Cards':
            case 'Drivers':
            case 'Expense Types':
            case 'Hold Notes':
            case 'Order Payment Methods':
            case 'Part Service Charges':
            case 'Part Type':
            case 'Trailers':
            case 'Transport Plates':
            case 'Trucks':
            case 'Vendor Payment Methods': {
                this.gridColumns = [{ dataField: 'name', caption: 'Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true }];
                break;
            }
            case 'Dealer Terms':
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'quickBooksTerm', width: 140 },
                ];
                break;
            case 'Invoice Payment Methods':
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'quickBooksPaymentMethod', width: 160 },
                    { dataField: 'isFloorplanMethod', caption: 'Is Floorplan', width: 100 },
                    { width: 100 },
                ];
                break;
            case 'Delivery Options':
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 120, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'description', width: 200 },
                    { dataField: 'requiresFreight', width: 80 },
                    { dataField: 'dealerOption', width: 80 },
                    { dataField: 'dealerConfirmationMessage', width: 200 },
                    { dataField: 'dealerAcknowledgementMessage', caption: 'Dealer Acknowledgement Message', width: 200 },
                    { dataField: 'acknowledgementRequired', caption: 'Acknowledgement Required', width: 80 },
                ];
                break;
            case 'Pricing Tiers':
                this.gridColumns = [
                    { dataField: 'name', width: 175, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'percentage', width: 100, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'minimumAmount', width: 100, format: { type: 'currency', precision: 2 }, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'maximumAmount', width: 100, format: { type: 'currency', precision: 2 }, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'inactive', width: 100, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'description', width: 175, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'notes', width: 175, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                ];
                break;
            case 'Confirmation Payment Methods':
                this.gridColumns = [
                    { dataField: 'name', width: 250 },
                    { dataField: 'requiresAch', caption: 'Requires ACH', width: 140 },
                    { dataField: 'requiresNorthpoint', width: 140 },
                    { dataField: 'winterOrder', width: 140 },
                    { dataField: 'visibleToChannelPartner', width: 140 },
                    { dataField: 'paymentTerms' },
                ];
                break;
            case 'Purchase Order Line Items': {
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'type', caption: 'Type', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    {
                        dataField: 'quickBooksAccount',
                        caption: 'QuickBooks Account',
                        width: 200,
                        allowSorting: false,
                        allowEditing: false,
                        allowHeaderFiltering: true,
                    },
                ];
                break;
            }
            case 'Sales Rep': {
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'displayName', caption: 'Display Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    {
                        dataField: 'sendSalesOrders',
                        caption: 'Send Sales Orders',
                        width: 140,
                        allowSorting: false,
                        allowEditing: false,
                        allowHeaderFiltering: true,
                    },
                    { dataField: 'email', caption: 'Email', allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                ];
                break;
            }
            case 'State Tax Exemptions': {
                this.gridColumns = [
                    { dataField: 'name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'url', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                ];
                break;
            }
            case 'Email Templates': {
                this.gridColumns = [
                    { dataField: 'name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'subject', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'body', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                ];
                break;
            }
            case 'Fee Types': {
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'quickBooksInvoiceAccount', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'quickBooksPaymentAccount', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'percentageBased', width: 100, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'amountBased', width: 100, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'defaultAmount', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                ];
                break;
            }
            case 'Vendor Terms': {
                this.gridColumns = [
                    { dataField: 'name', caption: 'Name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'daysOffset', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true, alignment: 'left' },
                ];
                break;
            }
            case 'Purchase Order Shipping Addresses': {
                this.gridColumns = [
                    { dataField: 'name', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'companyName', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'address1', width: 200, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'address2', width: 75, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'city', width: 75, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'state', width: 75, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'zip', width: 75, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                    { dataField: 'phone', width: 50, allowSorting: false, allowEditing: false, allowHeaderFiltering: true },
                ];
                break;
            }
            default: {
                this.gridColumns = [];
                break;
            }
        }
    }

    async getData() {
        switch (this.entityType) {
            case 'Assembly Jigs': {
                this.entities = await this.uow.assemblyJigs.whereActive(this.orderProperty);
                break;
            }
            case 'Drivers': {
                this.entities = await this.uow.deliveryDrivers.whereActive(this.orderProperty);
                break;
            }
            case 'Expense Types': {
                this.entities = await this.uow.expenseTypes.whereActive(this.orderProperty);
                break;
            }
            case 'Hold Notes': {
                this.entities = await this.uow.holdNotes.whereActive(this.orderProperty);
                break;
            }
            case 'Part Type': {
                this.entities = await this.uow.partTypes.whereActive(this.orderProperty);
                break;
            }
            case 'Part Service Charges': {
                this.entities = await this.uow.partServiceCharges.whereActive(this.orderProperty);
                break;
            }
            case 'Invoice Payment Methods': {
                this.entities = await this.uow.invoicePaymentMethods.whereActive(this.orderProperty);
                break;
            }
            case 'Pricing Tiers': {
                this.entities = await this.uow.pricingTiers.whereActive(this.orderProperty);
                break;
            }
            case 'Purchase Order Line Items': {
                this.entities = await this.uow.purchaseOrderLineItems.whereActive(this.orderProperty);
                break;
            }
            case 'Purchase Order Shipping Addresses': {
                this.entities = await this.uow.purchaseOrderShipAddresses.whereActive(this.orderProperty);
                break;
            }
            case 'Order Payment Methods': {
                this.entities = await this.uow.orderPaymentMethods.whereActive(this.orderProperty);
                break;
            }
            case 'Sales Rep': {
                this.entities = await this.uow.salesReps.whereActive(this.orderProperty);
                break;
            }
            case 'Confirmation Payment Methods': {
                this.entities = await this.uow.confirmationPaymentMethods.whereActive(this.orderProperty);
                break;
            }
            case 'Dealer Terms': {
                this.entities = await this.uow.dealerTerms.whereActive(this.orderProperty);
                break;
            }
            case 'Delivery Options': {
                this.entities = await this.uow.deliveryOptions.whereActive(this.orderProperty);
                break;
            }
            case 'State Tax Exemptions': {
                this.entities = await this.uow.stateTaxExemptions.whereActive(this.orderProperty);
                break;
            }
            case 'Trailers': {
                this.entities = await this.uow.deliveryTrailers.whereActive(this.orderProperty);
                break;
            }
            case 'Trucks': {
                this.entities = await this.uow.deliveryTrucks.whereActive(this.orderProperty);
                break;
            }
            case 'Transport Plates': {
                this.entities = await this.uow.deliveryTransportPlates.whereActive(this.orderProperty);
                break;
            }
            case 'Fee Types': {
                this.entities = await this.uow.feeTypes.whereActive(this.orderProperty);
                break;
            }
            case 'Credit Cards': {
                this.entities = await this.uow.creditCards.whereActive(this.orderProperty);
                break;
            }
            case 'Email Templates': {
                this.entities = await this.uow.emailTemplates.whereActive(this.orderProperty);
                break;
            }
            case 'Vendor Payment Methods': {
                this.entities = await this.uow.vendorPaymentMethods.whereActive(this.orderProperty);
                break;
            }
            case 'Vendor Terms': {
                this.entities = await this.uow.vendorTerms.whereActive(this.orderProperty);
                break;
            }
            default:
                this.entities = [];
                break;
        }
    }

    getMaxDisplayOrder() {
        if (!this.entities || this.entities.length === 0) return 1;
        const maxEntity = _.maxBy(this.entities, this.orderProperty);
        return (maxEntity ? (maxEntity[this.orderProperty] ?? 0) : 0) + 1;
    }

    async open(entity?: any) {
        switch (this.entityType) {
            case 'Assembly Jigs': {
                if (!entity) {
                    entity = this.uow.assemblyJigs.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Drivers': {
                if (!entity) {
                    entity = this.uow.deliveryDrivers.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Expense Types': {
                if (!entity) {
                    entity = this.uow.expenseTypes.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Hold Notes': {
                if (!entity) {
                    entity = this.uow.holdNotes.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Part Type': {
                if (!entity) {
                    entity = this.uow.partTypes.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Part Service Charges': {
                if (!entity) {
                    entity = this.uow.partServiceCharges.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Invoice Payment Methods': {
                await this.openInvoicePaymentMethod(entity);
                break;
            }
            case 'Purchase Order Line Items': {
                if (!entity) {
                    entity = this.uow.purchaseOrderLineItems.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.openPurchaseOrderLineItem(entity);
                break;
            }
            case 'Order Payment Methods': {
                if (!entity) {
                    entity = this.uow.orderPaymentMethods.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Sales Rep': {
                await this.setSalesRep(entity);
                break;
            }
            case 'Confirmation Payment Methods': {
                await this.openConfirmationPaymentMethod(entity);
                break;
            }
            case 'Dealer Terms': {
                await this.openDealerTerm(entity);
                break;
            }
            case 'Delivery Options': {
                await this.openDeliveryOption(entity);
                break;
            }
            case 'State Tax Exemptions': {
                await this.openStateTaxExemption(entity);
                break;
            }
            case 'Trailers': {
                if (!entity) {
                    entity = this.uow.deliveryTrailers.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Trucks': {
                if (!entity) {
                    entity = this.uow.deliveryTrucks.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Transport Plates': {
                if (!entity) {
                    entity = this.uow.deliveryTransportPlates.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Credit Cards': {
                if (!entity) {
                    entity = this.uow.creditCards.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Fee Types': {
                await this.openFeeType(entity);
                break;
            }
            case 'Vendor Payment Methods': {
                if (!entity) {
                    entity = this.uow.vendorPaymentMethods.createEntity();
                    entity[this.orderProperty] = this.getMaxDisplayOrder();
                }
                this.setProperty(entity);
                break;
            }
            case 'Email Templates': {
                await this.openEmailTemplate(entity);
                break;
            }
            case 'Vendor Terms': {
                await this.openVendorTerm(entity);
                break;
            }
            case 'Pricing Tiers': {
                await this.openPricingTier(entity);
                break;
            }
            case 'Purchase Order Shipping Addresses': {
                await this.openPurchaseOrderShippingAddress(entity);
                break;
            }
        }
    }

    setProperty(entity) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: this.entityType,
                dialogPrompt: `Enter ${this.valuePrompt}`,
                dialogValue: entity[this.valueProperty],
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                entity[this.valueProperty] = result.value;
                await this.uow.commit();
                this.getData();
            } else {
                this.uow.rollback();
            }
            modalRef.hide();
        });
    }

    async setSalesRep(entity?: any) {
        const modalRef = this.modalService.show(SalesRepDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                salesRep: entity,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }
    async openDealerTerm(dealerTerm?: DealerTerm) {
        const modalRef = this.modalService.show(DealerTermDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dealerTerm: dealerTerm,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openConfirmationPaymentMethod(confirmationPaymentMethod?: ConfirmationPaymentMethod) {
        const modalRef = this.modalService.show(ConfirmationPaymentMethodDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                confirmationPaymentMethod: confirmationPaymentMethod,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openDeliveryOption(deliveryOption?: DeliveryOption) {
        const modalRef = this.modalService.show(DeliveryOptionDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                deliveryOption: deliveryOption,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openInvoicePaymentMethod(invoicePaymentMethod?: InvoicePaymentMethod) {
        const modalRef = this.modalService.show(InvoicePaymentMethodDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                invoicePaymentMethod: invoicePaymentMethod,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openStateTaxExemption(stateTaxExemption: StateTaxExemption) {
        const modalRef = this.modalService.show(StateTaxExemptionDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                stateTaxExemption: stateTaxExemption,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openPurchaseOrderLineItem(purchaseOrderLineItem: PurchaseOrderLineItem) {
        const modalRef = this.modalService.show(LineItemDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                lineItem: purchaseOrderLineItem,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openFeeType(entity?: any) {
        const modalRef = this.modalService.show(FeeTypeDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                feeType: entity,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openEmailTemplate(emailTemplate?: EmailTemplate) {
        const modalRef = this.modalService.show(EmailTemplateDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                emailTemplate: emailTemplate,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openVendorTerm(vendorTerm?: VendorTerm) {
        const modalRef = this.modalService.show(VendorTermDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                vendorTerm: vendorTerm,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openPricingTier(pricingTier?: PricingTier) {
        const modalRef = this.modalService.show(PricingTierDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                pricingTier: pricingTier,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async openPurchaseOrderShippingAddress(address?: PurchaseOrderShipAddress) {
        const modalRef = this.modalService.show(PurchaseOrderShipAddressDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                address: address,
                displayOrder: this.getMaxDisplayOrder(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async delete(entity) {
        if (!entity) return;
        if (this.entityType === 'Part Type') {
            // this same approach for any entity type that has deleted field.
            if (entity.id === PartType.Types.Finished || entity.id === PartType.Types.Trailer || entity.id === PartType.Types.Replacement) {
                await alert(
                    `PartTypes 'Finished', 'Trailer', and 'Replacement' cannot be deleted because they are used in the code other places.`,
                    `${this.appInfo.settings.company}`,
                );
                return;
            }
        }

        const result = await (confirm(`Are you sure you want to DELETE this item?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!!result) {
            entity.deleted = new Date();
            await this.uow.commit();
            await this.getData();
        }
    }

    async rowDoubleClick(e) {
        if (e.rowType === 'data') {
            await this.open(e.data);
        }
    }

    tripRecordUploadStarted() {
        this.appInfo.pleaseWaitShow();
    }

    async tripRecordUploaded() {
        this.driverTripRecordUrlLastUpdate = (await this.uow.getSettingsByName('DriverTripRecordUrlLastUpdate')) as Setting;
        this.appInfo.pleaseWaitHide();
        this.appInfo.notifyToast('File uploaded successfully.', 'success');
    }

    tripRecordUploadError() {
        this.appInfo.pleaseWaitHide();
        this.appInfo.notifyToast('An error occurred while uploading the file.', 'error');
    }

    onReorder = async (e) => {
        const visibleRows = e.component.getVisibleRows(),
            toIndex = this.entities.indexOf(visibleRows[e.toIndex].data),
            fromIndex = this.entities.indexOf(e.itemData);

        this.entities.splice(fromIndex, 1);
        this.entities.splice(toIndex, 0, e.itemData);

        for (let idx = 0; idx < this.entities.length; idx++) {
            this.entities[idx][this.orderProperty] = idx + 1;
        }
        await this.uow.commit();
    };
}
